import { useEffect, useState } from 'react'
import {
  createFileRoute,
  Outlet,
  redirect,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import { useProperties } from '../hooks/useProperties'
import { useZones } from '../hooks/useZones'
import { useMe } from '../hooks/useAuth'
import { calendarDaysUntil } from '../lib/dateRelative'
import { canWriteClient } from '../lib/permissions'
import { effectivePrice } from '../lib/price'
import { CardSkeleton } from '../components/Skeleton'
import { EmptyState } from '../components/EmptyState'
import { PropertyCreateModal } from '../components/PropertyCreateModal'
import { SavedViewsDropdown } from '../components/SavedViewsDropdown'
import { MutuabileBadge } from '../components/MutuabileBadge'
import {
  PROPERTY_SOURCE_LABEL,
  SOPRALLUOGO_BLOCK_LABEL,
  type PropertyRow,
} from '../lib/api'

type LottiSearch = {
  page: number
  page_size: number
  q: string
  source?: string
  stage?: string
  provincia?: string
  prezzo_min?: number
  prezzo_max?: number
  tribunale?: string
  tipologia?: string
  modalita_vendita?: string
  occupazione?: string
  residenziale?: boolean
  mutuabile?: boolean
  zona_tag?: string
  data_asta_from?: string
  data_asta_to?: string
  /** Default false: filtro "solo aste future" attivo. true = include passate+null. */
  include_past?: boolean
  sort: string
}

export const Route = createFileRoute('/lotti')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  validateSearch: (search: Record<string, unknown>): LottiSearch => {
    const toNum = (v: unknown, dflt: number): number => {
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n > 0 ? n : dflt
    }
    const toOptNum = (v: unknown): number | undefined => {
      if (v === undefined || v === null || v === '') return undefined
      const n = typeof v === 'number' ? v : Number(v)
      return Number.isFinite(n) && n >= 0 ? n : undefined
    }
    const toStr = (v: unknown): string => (typeof v === 'string' ? v : '')
    const toOptStr = (v: unknown): string | undefined =>
      typeof v === 'string' && v.length > 0 ? v : undefined
    const toOptBool = (v: unknown): boolean | undefined => {
      if (v === true || v === 'true') return true
      if (v === false || v === 'false') return false
      return undefined
    }
    return {
      page: toNum(search.page, 1),
      page_size: toNum(search.page_size, 50),
      q: toStr(search.q),
      source: toOptStr(search.source),
      stage: toOptStr(search.stage),
      provincia: toOptStr(search.provincia),
      prezzo_min: toOptNum(search.prezzo_min),
      prezzo_max: toOptNum(search.prezzo_max),
      tribunale: toOptStr(search.tribunale),
      tipologia: toOptStr(search.tipologia),
      modalita_vendita: toOptStr(search.modalita_vendita),
      occupazione: toOptStr(search.occupazione),
      residenziale: toOptBool(search.residenziale),
      mutuabile: toOptBool(search.mutuabile),
      zona_tag: toOptStr(search.zona_tag),
      data_asta_from: toOptStr(search.data_asta_from),
      data_asta_to: toOptStr(search.data_asta_to),
      include_past:
        search.include_past === true || search.include_past === 'true'
          ? true
          : undefined,
      sort: toStr(search.sort) || '-data_asta',
    }
  },
  component: LottiPage,
})

const STAGE_LABEL: Record<string, string> = {
  mappatura: 'Mappatura',
  bacheca: 'Bacheca',
  scd_richiesta: 'SCD richiesta',
  scd_pronta_fissare: 'SCD pronta',
  scd_fissate: 'SCD fissate',
  attesa_esito: 'Attesa esito',
  esito_pos_checklist: 'Esito pos. → checklist',
  domanda_asta: 'Domanda asta',
  data_vendita: 'Data vendita',
  non_aggiudicata: 'Non aggiudicata',
  aggiudicata: 'Aggiudicata',
  esito_neg_scd: 'Esito neg. SCD',
  scartata: 'Scartata',
}

const STAGE_CLASS: Record<string, string> = {
  bacheca: 'bacheca',
  scd_richiesta: 'scd-richiesta',
  scd_pronta_fissare: 'scd-pronta',
  scd_fissate: 'scd-fissate',
  attesa_esito: 'attesa-esito',
  esito_pos_checklist: 'esito-pos',
  domanda_asta: 'domanda-asta',
  data_vendita: 'data-vendita',
  esito_neg_scd: 'esito-neg',
  non_aggiudicata: 'non-aggiudicata',
  aggiudicata: 'aggiudicata',
}

const SAVED_VIEWS = [
  { key: 'all', label: 'In pipeline', stage: undefined },
  { key: 'bacheca', label: 'Solo Bacheca', stage: 'bacheca' },
  { key: 'scd', label: 'SCD in corso', stage: 'scd_fissate' },
  { key: 'aste7', label: 'Aste 7gg', stage: 'domanda_asta' },
  { key: 'aggiudicata', label: 'Aggiudicate', stage: 'aggiudicata' },
  { key: 'non_agg', label: 'Non aggiudicate', stage: 'non_aggiudicata' },
]

function fmtCurrencyShort(v: number | null): string {
  if (v === null || v === undefined) return '—'
  if (v >= 1000) {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(v)
  }
  return `€ ${v}`
}

function fmtDateShort(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${String(d.getFullYear()).slice(2)}`
}

function fmtTime(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

function shortCode(code: number | null | undefined): string {
  if (code === null || code === undefined) return ''
  return `L_${code.toString().padStart(5, '0')}`
}

function countdownInfo(iso: string | null): { label: string; tone: 'soon' | 'next' | 'far' | 'past' } | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  if (d.getTime() - Date.now() < 0) return { label: 'Asta passata', tone: 'past' }
  const days = calendarDaysUntil(d)
  if (days === 0) return { label: 'Oggi', tone: 'soon' }
  if (days === 1) return { label: 'Domani', tone: 'soon' }
  if (days <= 3) return { label: `${days} giorni`, tone: 'soon' }
  if (days <= 7) return { label: `${days} giorni`, tone: 'next' }
  return { label: `${days} giorni`, tone: 'far' }
}

function modalitaTag(modalita: string | null): string {
  if (!modalita) return ''
  const m = modalita.toLowerCase()
  if (m.includes('telematica')) return 'tab-telematica'
  if (m.includes('fisica')) return 'tab-fisica'
  if (m.includes('mista')) return 'tab-mista'
  return ''
}

function modalitaLabel(modalita: string | null): string {
  if (!modalita) return ''
  const m = modalita.toLowerCase()
  if (m.includes('telematica')) return 'TELEMATICA'
  if (m.includes('fisica')) return 'FISICA'
  if (m.includes('mista')) return 'MISTA'
  return modalita.toUpperCase()
}

function occupazioneClass(occ: string | null): string {
  if (!occ) return ''
  const o = occ.toLowerCase()
  if (o.includes('libero')) return 'libero'
  if (o.includes('esecutato')) return 'esecutato'
  if (o.includes('liberazione')) return 'liberazione'
  return ''
}

function ribasso(prezzoBase: number | null, valoreMercato: number | null): { label: string; cls: string } | null {
  if (!prezzoBase || !valoreMercato || valoreMercato === 0) return null
  const pct = Math.round(((prezzoBase - valoreMercato) / valoreMercato) * 100)
  if (pct === 0) return null
  if (pct < 0) return { label: `${pct}% vs perizia`, cls: 'down' }
  return { label: `+${pct}% vs perizia`, cls: 'up' }
}

function LottiPage() {
  const matchRoute = useMatchRoute()
  const onChild =
    matchRoute({ to: '/lotti/$id', fuzzy: true }) ||
    matchRoute({ to: '/lotti/$id/match' })
  if (onChild) return <Outlet />
  return <LottiList />
}

function LottiList() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: '/lotti' })
  const { data: me } = useMe()
  const canCreate = canWriteClient(me)
  const [createOpen, setCreateOpen] = useState(false)
  const [qLocal, setQLocal] = useState(search.q)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [density, setDensity] = useState<'comoda' | 'densa'>('comoda')

  useEffect(() => setQLocal(search.q), [search.q])
  useEffect(() => {
    const t = setTimeout(() => {
      if (qLocal !== search.q) {
        void navigate({ search: { ...search, q: qLocal, page: 1 }, replace: true })
      }
    }, 600)
    return () => clearTimeout(t)
  }, [qLocal, search, navigate])

  const zonesQuery = useZones()
  const zonaOptions = [
    { value: '', label: 'Tutte le zone' },
    { value: '__none__', label: '(Senza zona)' },
    ...(zonesQuery.data?.items ?? []).map((z) => ({
      value: z.tag,
      label: z.tag,
    })),
  ]

  const { data, isLoading, isError } = useProperties({
    page: search.page,
    page_size: search.page_size,
    q: search.q || undefined,
    stage: search.stage,
    provincia: search.provincia,
    prezzo_min: search.prezzo_min,
    prezzo_max: search.prezzo_max,
    source: search.source,
    tribunale: search.tribunale,
    tipologia: search.tipologia,
    residenziale: search.residenziale,
    mutuabile: search.mutuabile,
    zona_tag: search.zona_tag,
    // Default: solo aste future (data_asta >= today). Toggle chip
    // "Includi passate" disabilita il filtro impostando include_past=true.
    data_asta_from: search.include_past
      ? search.data_asta_from
      : search.data_asta_from ?? new Date().toISOString().slice(0, 10),
    data_asta_to: search.data_asta_to,
    sort: search.sort,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const pages = data?.pages ?? 1

  const activeView = SAVED_VIEWS.find((v) => v.stage === search.stage) ?? SAVED_VIEWS[0]
  const filterCount = [
    search.q,
    search.stage,
    search.provincia,
    search.prezzo_min,
    search.prezzo_max,
    search.source,
    search.zona_tag,
    search.residenziale,
    search.mutuabile,
  ].filter((v) => v !== undefined && v !== '').length

  function setView(view: typeof SAVED_VIEWS[number]) {
    void navigate({ search: { ...search, stage: view.stage, page: 1 }, replace: true })
    setSelected(new Set())
  }

  function setSort(s: string) {
    void navigate({ search: { ...search, sort: s, page: 1 }, replace: true })
  }

  function goToPage(p: number) {
    void navigate({ search: { ...search, page: p }, replace: true })
  }

  function openProperty(id: string) {
    void navigate({ to: '/lotti/$id', params: { id } } as never)
  }

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleSelectAll() {
    setSelected((prev) =>
      prev.size === items.length ? new Set() : new Set(items.map((p) => p.id)),
    )
  }

  function resetFilters() {
    void navigate({
      search: { page: 1, page_size: 50, q: '', sort: '-data_asta' },
      replace: true,
    })
  }

  if (isLoading) return <CardSkeleton />
  if (isError) {
    return (
      <EmptyState
        icon="⚠️"
        title="Errore caricamento lotti"
        body="Riprova fra qualche secondo."
      />
    )
  }

  const selectedCount = selected.size

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb">
            Operativo · <span className="here">Lotti</span>
          </div>
          <div className="page-title-row">
            <h1>Lotti</h1>
            <span className="total">
              <b>{total.toLocaleString('it-IT')}</b>{' '}
              {total === 1 ? 'lotto' : 'lotti'} totali
              {activeView.key !== 'all' && ` · vista «${activeView.label}»`}
            </span>
          </div>
        </div>
        <div className="page-actions">
          <button type="button" className="btn">
            <SvgDownload />
            Esporta CSV
          </button>
          {canCreate && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setCreateOpen(true)}
              title="Da URL (auto-parse) o Manuale"
            >
              <SvgPlus />
              Nuovo lotto
            </button>
          )}
        </div>
      </div>

      <div className="savedviews">
        {SAVED_VIEWS.map((v) => (
          <a
            key={v.key}
            className={`sv-tab${activeView.key === v.key ? ' active' : ''}`}
            onClick={() => setView(v)}
          >
            {v.label}
          </a>
        ))}
        <span className="sv-divider" />
        <a
          className={`sv-tab${!search.include_past ? ' active' : ''}`}
          title={
            search.include_past
              ? 'Toggle: ora include aste passate e senza data — clicca per nascondere'
              : 'Default: solo lotti con data asta certa e futura (>= oggi). Clicca per includere passate.'
          }
          onClick={() =>
            void navigate({
              search: {
                ...search,
                include_past: search.include_past ? undefined : true,
                data_asta_from: undefined,
                page: 1,
              },
              replace: true,
            })
          }
        >
          📅 Solo aste future
          {!search.include_past && <span className="ct">{total}</span>}
        </a>
        <span className="sv-divider" />
        <SavedViewsDropdown
          entityType="lotti"
          currentFilters={search}
          onApply={(filters) =>
            navigate({
              search: { ...search, ...filters, page: 1 },
              replace: true,
            })
          }
          label="+ Salva vista"
        />
      </div>

      <div className="filterbar">
        <div className="filterbar-row">
          <div className="fb-search">
            <span className="ico">
              <SvgSearch />
            </span>
            <input
              type="text"
              value={qLocal}
              onChange={(e) => setQLocal(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && qLocal !== search.q) {
                  void navigate({ search: { ...search, q: qLocal, page: 1 }, replace: true })
                }
              }}
              placeholder="Cerca per codice L_, indirizzo, RGE, tribunale…"
              aria-label="Ricerca lotti"
            />
          </div>

          <FilterChipSelect
            label="Provincia"
            value={search.provincia}
            displayValue={search.provincia}
            options={[
              { value: '', label: 'Tutte' },
              { value: 'RM', label: 'Roma (RM)' },
              { value: 'FR', label: 'Frosinone' },
              { value: 'LT', label: 'Latina' },
              { value: 'RI', label: 'Rieti' },
              { value: 'VT', label: 'Viterbo' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, provincia: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Tribunale"
            value={search.tribunale}
            displayValue={search.tribunale}
            options={[
              { value: '', label: 'Tutti' },
              { value: 'Roma', label: 'Roma' },
              { value: 'Velletri', label: 'Velletri' },
              { value: 'Tivoli', label: 'Tivoli' },
              { value: 'Civitavecchia', label: 'Civitavecchia' },
              { value: 'Frosinone', label: 'Frosinone' },
              { value: 'Latina', label: 'Latina' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, tribunale: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Tipologia"
            value={search.tipologia}
            displayValue={search.tipologia}
            options={[
              { value: '', label: 'Tutte' },
              { value: 'APPARTAMENTO', label: 'Appartamento' },
              { value: 'VILLA', label: 'Villa' },
              { value: 'NEGOZIO', label: 'Negozio' },
              { value: 'MAGAZZINO', label: 'Magazzino' },
              { value: 'TERRENO', label: 'Terreno' },
              { value: 'BOX', label: 'Box / Garage' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, tipologia: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Residenziale"
            value={
              search.residenziale === undefined
                ? undefined
                : String(search.residenziale)
            }
            displayValue={
              search.residenziale === true
                ? 'Solo residenziali'
                : search.residenziale === false
                  ? 'Solo non residenziali'
                  : undefined
            }
            options={[
              { value: '', label: 'Tutti' },
              { value: 'true', label: 'Solo residenziali' },
              { value: 'false', label: 'Solo non residenziali (box, cantina, negozio…)' },
            ]}
            onChange={(v) =>
              navigate({
                search: {
                  ...search,
                  residenziale: v === '' ? undefined : v === 'true',
                  page: 1,
                },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Mutuabile"
            value={
              search.mutuabile === undefined ? undefined : String(search.mutuabile)
            }
            displayValue={
              search.mutuabile === true
                ? 'Solo mutuabili'
                : search.mutuabile === false
                  ? 'Non mutuabili'
                  : undefined
            }
            options={[
              { value: '', label: 'Tutti' },
              { value: 'true', label: '🏦 Solo mutuabili' },
              { value: 'false', label: 'Non mutuabili' },
            ]}
            onChange={(v) =>
              navigate({
                search: {
                  ...search,
                  mutuabile: v === '' ? undefined : v === 'true',
                  page: 1,
                },
                replace: true,
              })
            }
          />
          <FilterChipPrezzo
            min={search.prezzo_min}
            max={search.prezzo_max}
            onApply={(min, max) =>
              navigate({
                search: {
                  ...search,
                  prezzo_min: min,
                  prezzo_max: max,
                  page: 1,
                },
                replace: true,
              })
            }
          />
          <FilterChipDataAsta
            from={search.data_asta_from}
            to={search.data_asta_to}
            onApply={(from, to) =>
              navigate({
                search: {
                  ...search,
                  data_asta_from: from,
                  data_asta_to: to,
                  page: 1,
                },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Stato"
            value={search.stage}
            displayValue={search.stage ? STAGE_LABEL[search.stage] : undefined}
            options={[
              { value: '', label: 'Tutti' },
              { value: 'bacheca', label: 'Bacheca' },
              { value: 'scd_richiesta', label: 'SCD richiesta' },
              { value: 'scd_pronta_fissare', label: 'SCD pronta' },
              { value: 'scd_fissate', label: 'SCD fissate' },
              { value: 'attesa_esito', label: 'Attesa esito' },
              { value: 'esito_pos_checklist', label: 'Esito pos.' },
              { value: 'domanda_asta', label: 'Domanda asta' },
              { value: 'data_vendita', label: 'Data vendita' },
              { value: 'aggiudicata', label: 'Aggiudicata' },
              { value: 'non_aggiudicata', label: 'Non aggiudicata' },
              { value: 'scartata', label: 'Scartata' },
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, stage: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Zona AGL"
            value={search.zona_tag}
            displayValue={
              search.zona_tag === '__none__' ? '(Senza zona)' : search.zona_tag
            }
            options={zonaOptions}
            onChange={(v) =>
              navigate({
                search: { ...search, zona_tag: v || undefined, page: 1 },
                replace: true,
              })
            }
          />
          <FilterChipSelect
            label="Fonte"
            value={search.source}
            displayValue={
              search.source
                ? ((PROPERTY_SOURCE_LABEL as Record<string, string>)[
                    search.source
                  ] ?? search.source)
                : undefined
            }
            options={[
              { value: '', label: 'Tutte' },
              ...Object.entries(PROPERTY_SOURCE_LABEL).map(([value, label]) => ({
                value,
                label,
              })),
            ]}
            onChange={(v) =>
              navigate({
                search: { ...search, source: v || undefined, page: 1 },
                replace: true,
              })
            }
          />

          {filterCount > 0 && (
            <>
              <span className="fb-divider" />
              <button type="button" className="fb-clear" onClick={resetFilters}>
                Reset filtri ({filterCount})
              </button>
            </>
          )}
        </div>
        <div className="fb-meta">
          <span>
            <span className="strong">{total.toLocaleString('it-IT')}</span> lotti totali ·{' '}
            <span className="strong">{items.length}</span> visibili in pagina
          </span>
          <div className="right">
            <span>Densità:</span>
            <div className="segmented" style={{ border: 0, padding: 0 }}>
              <button
                type="button"
                className={density === 'comoda' ? 'on' : ''}
                onClick={() => setDensity('comoda')}
              >
                Comoda
              </button>
              <button
                type="button"
                className={density === 'densa' ? 'on' : ''}
                onClick={() => setDensity('densa')}
              >
                Densa
              </button>
            </div>
            <span className="fb-divider" style={{ width: 1, height: 14 }} />
            <span>
              Ordinato per{' '}
              <select
                value={search.sort}
                onChange={(e) => setSort(e.target.value)}
                style={{
                  border: 0,
                  background: 'transparent',
                  fontWeight: 'var(--fw-medium)',
                  color: 'var(--ink-1)',
                  cursor: 'pointer',
                }}
              >
                <option value="-data_asta">Data asta ↓</option>
                <option value="data_asta">Data asta ↑</option>
                <option value="-prezzo_base">Prezzo ↓</option>
                <option value="prezzo_base">Prezzo ↑</option>
                <option value="-created_at">Recenti</option>
              </select>
            </span>
          </div>
        </div>
      </div>

      {selectedCount > 0 && (
        <div className="bulkbar">
          <span className="selcount">{selectedCount} selezionati</span>
          <button type="button" className="bba">Sposta in stage</button>
          <button type="button" className="bba">Match con cliente…</button>
          <button type="button" className="bba">Esporta selezione</button>
          <button type="button" className="bba danger">Archivia</button>
          <span className="spacer" />
          <button
            type="button"
            className="deselect"
            onClick={() => setSelected(new Set())}
            style={{ background: 'transparent', border: 0, cursor: 'pointer' }}
          >
            Deseleziona tutti
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <EmptyState
          icon="🏠"
          title="Nessun lotto matcha i filtri"
          body="Modifica filtri o resetta per vedere tutti i lotti."
          action={{ label: 'Reset filtri', onClick: resetFilters }}
        />
      ) : (
        <div className={`table-card${density === 'densa' ? ' dense' : ''}`}>
          <table className="tbl">
            <thead>
              <tr>
                <th className="check-col">
                  <span
                    className={`cbox${
                      selectedCount === items.length
                        ? ' checked'
                        : selectedCount > 0
                        ? ' indeterminate'
                        : ''
                    }`}
                    onClick={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th>Codice</th>
                <th>Lotto / Indirizzo</th>
                <th>Procedura</th>
                <th className="num">m²</th>
                <th className="num">Prezzo base</th>
                <th>Data asta</th>
                <th>Stato pipeline</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <LottoRow
                  key={p.id}
                  p={p}
                  selected={selected.has(p.id)}
                  onToggle={() => toggleSelect(p.id)}
                  onOpen={() => openProperty(p.id)}
                />
              ))}
            </tbody>
          </table>

          <div className="pagination">
            <div className="pgleft">
              <span>
                · <b className="mono" style={{ color: 'var(--ink-1)' }}>
                  {(search.page - 1) * search.page_size + 1}–
                  {(search.page - 1) * search.page_size + items.length}
                </b>{' '}
                di{' '}
                <b className="mono" style={{ color: 'var(--ink-1)' }}>
                  {total.toLocaleString('it-IT')}
                </b>
              </span>
            </div>
            <div className="pgnav">
              <button
                type="button"
                className={search.page <= 1 ? 'disabled' : ''}
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                ←
              </button>
              <span className="muted mono" style={{ padding: '0 var(--sp-2)' }}>
                {search.page} / {pages}
              </span>
              <button
                type="button"
                className={search.page >= pages ? 'disabled' : ''}
                disabled={search.page >= pages}
                onClick={() => goToPage(search.page + 1)}
              >
                →
              </button>
            </div>
          </div>
        </div>
      )}

      <PropertyCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  )
}

function FilterChipSelect({
  label,
  value,
  displayValue,
  options,
  onChange,
}: {
  label: string
  value: string | undefined
  displayValue?: string
  options: Array<{ value: string; label: string }>
  onChange: (v: string) => void
}) {
  const hasValue = Boolean(value)
  return (
    <label
      className={`fb-chip${hasValue ? ' has-value' : ''}`}
      style={{ position: 'relative', cursor: 'pointer' }}
    >
      <span className="label">{label}</span>
      {hasValue && displayValue && <span className="val">{displayValue}</span>}
      <span className="caret">
        <SvgCaret />
      </span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          cursor: 'pointer',
          border: 0,
          background: 'transparent',
          appearance: 'none',
        }}
        aria-label={label}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  )
}

function FilterChipPrezzo({
  min,
  max,
  onApply,
}: {
  min: number | undefined
  max: number | undefined
  onApply: (min: number | undefined, max: number | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [minLocal, setMinLocal] = useState<string>(min?.toString() ?? '')
  const [maxLocal, setMaxLocal] = useState<string>(max?.toString() ?? '')
  const hasValue = min !== undefined || max !== undefined
  const display =
    hasValue
      ? `${min ? `€${(min / 1000).toFixed(0)}k` : '0'}–${max ? `€${(max / 1000).toFixed(0)}k` : '∞'}`
      : undefined

  function apply() {
    const minN = minLocal === '' ? undefined : Number(minLocal)
    const maxN = maxLocal === '' ? undefined : Number(maxLocal)
    onApply(
      minN !== undefined && Number.isFinite(minN) ? minN : undefined,
      maxN !== undefined && Number.isFinite(maxN) ? maxN : undefined,
    )
    setOpen(false)
  }

  function reset() {
    setMinLocal('')
    setMaxLocal('')
    onApply(undefined, undefined)
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className={`fb-chip${hasValue ? ' has-value' : ''}`}
        onClick={() => {
          if (!open) {
            setMinLocal(min?.toString() ?? '')
            setMaxLocal(max?.toString() ?? '')
          }
          setOpen((o) => !o)
        }}
      >
        <span className="label">Prezzo</span>
        {display && <span className="val">{display}</span>}
        <span className="caret">
          <SvgCaret />
        </span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 'var(--sp-3)',
            minWidth: 240,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Min €</span>
            <input
              type="number"
              min={0}
              value={minLocal}
              onChange={(e) => setMinLocal(e.target.value)}
              placeholder="0"
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Max €</span>
            <input
              type="number"
              min={0}
              value={maxLocal}
              onChange={(e) => setMaxLocal(e.target.value)}
              placeholder="∞"
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
            <button type="button" className="btn sm ghost" onClick={reset}>
              Reset
            </button>
            <button type="button" className="btn sm primary" onClick={apply}>
              Applica
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function FilterChipDataAsta({
  from,
  to,
  onApply,
}: {
  from: string | undefined
  to: string | undefined
  onApply: (from: string | undefined, to: string | undefined) => void
}) {
  const [open, setOpen] = useState(false)
  const [fromLocal, setFromLocal] = useState<string>(from ?? '')
  const [toLocal, setToLocal] = useState<string>(to ?? '')
  const hasValue = from !== undefined || to !== undefined
  const fmt = (d: string) => {
    const [, m, dd] = d.split('-')
    return dd && m ? `${dd}/${m}` : d
  }
  const display = hasValue
    ? `${from ? fmt(from) : '…'}–${to ? fmt(to) : '…'}`
    : undefined

  function apply() {
    onApply(fromLocal || undefined, toLocal || undefined)
    setOpen(false)
  }

  function reset() {
    setFromLocal('')
    setToLocal('')
    onApply(undefined, undefined)
    setOpen(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <button
        type="button"
        className={`fb-chip${hasValue ? ' has-value' : ''}`}
        onClick={() => {
          if (!open) {
            setFromLocal(from ?? '')
            setToLocal(to ?? '')
          }
          setOpen((o) => !o)
        }}
      >
        <span className="label">Data asta</span>
        {display && <span className="val">{display}</span>}
        <span className="caret">
          <SvgCaret />
        </span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            zIndex: 200,
            background: 'var(--surface)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-md)',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
            padding: 'var(--sp-3)',
            minWidth: 240,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Dal</span>
            <input
              type="date"
              value={fromLocal}
              max={toLocal || undefined}
              onChange={(e) => setFromLocal(e.target.value)}
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 'var(--fs-11)' }}>
            <span className="muted">Al</span>
            <input
              type="date"
              value={toLocal}
              min={fromLocal || undefined}
              onChange={(e) => setToLocal(e.target.value)}
              style={{ width: '100%', padding: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)' }}
            />
          </label>
          <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between' }}>
            <button type="button" className="btn sm ghost" onClick={reset}>
              Reset
            </button>
            <button type="button" className="btn sm primary" onClick={apply}>
              Applica
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function LottoRow({
  p,
  selected,
  onToggle,
  onOpen,
}: {
  p: PropertyRow
  selected: boolean
  onToggle: () => void
  onOpen: () => void
}) {
  const cd = countdownInfo(p.data_asta)
  const stageCls = STAGE_CLASS[p.stage] ?? 'bacheca'
  const tabCls = modalitaTag(p.modalita_vendita)
  const tabLabel = modalitaLabel(p.modalita_vendita)
  const occCls = occupazioneClass(p.occupazione)
  const rib = ribasso(p.prezzo_base, p.valore_mercato)
  const price = effectivePrice(p.offerta_minima, p.prezzo_base)
  const meta: string[] = []
  if (p.vani) meta.push(`${p.vani} vani`)
  if (p.piano) meta.push(`piano ${p.piano}`)
  return (
    <tr className={selected ? 'selected' : undefined}>
      <td className="check-col">
        <span
          className={`cbox${selected ? ' checked' : ''}`}
          onClick={onToggle}
          style={{ cursor: 'pointer' }}
        />
      </td>
      <td className="mono" onClick={onOpen} style={{ cursor: 'pointer' }}>
        <span className="tag code">{shortCode(p.short_code)}</span>
      </td>
      <td onClick={onOpen} style={{ cursor: 'pointer' }}>
        <div className="thumb-stack">
          <div className="thumb" />
          <div className="info">
            <div className="ln-1">
              <span className="title-text">
                {p.address ?? p.comune ?? p.title ?? 'Lotto'}
              </span>
              {p.backlog_count !== undefined && p.backlog_count > 0 && (
                <span
                  className="chip warn"
                  title={`${p.backlog_count} client${p.backlog_count === 1 ? 'e' : 'i'} da segnalare per questo lotto`}
                  style={{
                    marginLeft: 8,
                    fontSize: 'var(--fs-10)',
                    padding: '1px 7px',
                    background: 'oklch(96% 0.05 80)',
                    color: 'oklch(45% 0.15 60)',
                    border: '1px solid oklch(85% 0.08 80)',
                    borderRadius: 'var(--r-pill)',
                    fontWeight: 'var(--fw-semibold)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🔔 {p.backlog_count} da segnalare
                </span>
              )}
              {p.sopralluogo_block && (
                <span
                  className="chip risk"
                  title={`Sopralluoghi bloccati: ${SOPRALLUOGO_BLOCK_LABEL[p.sopralluogo_block] ?? p.sopralluogo_block}`}
                  style={{ marginLeft: 8, fontSize: 'var(--fs-10)' }}
                >
                  🚫 Bloccato
                </span>
              )}
              {p.is_residenziale === false && (
                <span
                  className="chip outline"
                  title={`Non residenziale${p.tipologia ? ` (${p.tipologia})` : ''} — box, posto auto, cantina, negozio o simili`}
                  style={{ marginLeft: 8, fontSize: 'var(--fs-10)' }}
                >
                  📦 Non residenziale
                </span>
              )}
              <MutuabileBadge lorenzoInviato={p.lorenzo_inviato} mutuabile={p.mutuabile} />
            </div>
            <div className="ln-2">
              {p.comune && (
                <>
                  <span>
                    {p.comune}
                    {p.zona_tag && ` · ${p.zona_tag}`}
                  </span>
                </>
              )}
              {p.occupazione && (
                <>
                  <span className="sep">·</span>
                  <span>
                    {occCls && <span className={`badge-mini ${occCls}`} />} {p.occupazione}
                  </span>
                </>
              )}
              {meta.length > 0 && (
                <>
                  <span className="sep">·</span>
                  <span>{meta.join(' · ')}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </td>
      <td>
        <div
          className="mono"
          style={{ fontSize: 'var(--fs-11)', color: 'var(--ink-2)' }}
        >
          {p.tribunale ?? '—'}
          {p.source_url && (
            <a
              href={p.source_url}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              title="Apri annuncio sorgente"
              style={{
                marginLeft: 6,
                color: 'var(--accent-ink)',
                textDecoration: 'none',
              }}
            >
              ↗
            </a>
          )}
        </div>
        {p.rge && (
          <div
            className="mono"
            style={{ fontSize: 'var(--fs-10)', color: 'var(--ink-3)' }}
          >
            RGE {p.rge}
          </div>
        )}
      </td>
      <td className="num mono" style={{ fontSize: 'var(--fs-12)' }}>
        {p.superficie_mq ?? '—'}
        {p.superficie_mq && (
          <span style={{ color: 'var(--ink-4)', fontSize: 'var(--fs-10)' }}>
            {' '}m²
          </span>
        )}
      </td>
      <td className="num">
        <div className="price">
          {price != null ? (
            fmtCurrencyShort(price)
          ) : (
            <span
              className="price-missing"
              title="Prezzo mancante: né offerta minima né base"
              style={{ color: 'var(--danger, #c0392b)', fontWeight: 700 }}
            >
              ⚠️ !
            </span>
          )}
          {rib && <span className={`ribasso ${rib.cls}`}>{rib.label}</span>}
        </div>
      </td>
      <td>
        {p.data_asta && (
          <div className={`aucdate ${cd?.tone ?? ''}`}>
            {fmtDateShort(p.data_asta)} · {fmtTime(p.data_asta)}
            {cd && <span className="when">{cd.label}</span>}
          </div>
        )}
        {tabLabel && (
          <div style={{ marginTop: 4 }}>
            <span className={`tag ${tabCls}`}>{tabLabel}</span>
          </div>
        )}
      </td>
      <td>
        <span className={`stage ${stageCls}`}>
          {STAGE_LABEL[p.stage] ?? p.stage}
        </span>
      </td>
      <td>
        <div className="row-actions">
          <button type="button" title="Apri" onClick={onOpen}>
            <SvgOpenLink />
          </button>
        </div>
      </td>
    </tr>
  )
}

function SvgSearch() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  )
}
function SvgPlus() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}
function SvgDownload() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  )
}
function SvgCaret() {
  return (
    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="m6 9 6 6 6-6" />
    </svg>
  )
}
function SvgOpenLink() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  )
}
